import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { CommonModal } from '../../../../global/modal/common/common-modal';
import { IntlShape } from 'react-intl';
import DefaultButton from '../../../../global/default-button/default-button';
import { DefaultCloseModalButton } from '../../../../global/modal/common/common-close-modal-buttons';
import RequestArtifactsPopoveriewModel from './request-artifacts-popover.viewmodel';

import './request-artifacts-popover.scss';
import { TRequestArtifactsTab } from '../../../../../api/artifacts.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { requestArtifactsTabLabels } from '../../../../../constants/artifacts.constants';
import { ArtifactsTab } from './artifacts-tab/artifacts-tab';
import { ReplaysTab } from './replays-tab/replays-tab';
import { RequestArtifactsPopoverHeader } from './request-artifacts-popover-header/request-artifacts-popover-header';

export interface IRequestArtifactsPopoverProps {
    isRequestArtifactsModalOpen: boolean;
    closePopover: () => void;
    intl: IntlShape;
    vm: RequestArtifactsPopoveriewModel;
}

export interface IRequestArtifactTabs {
    type: TRequestArtifactsTab;
    label: JSX.Element | string;
    permission: boolean;
}

const RequestArtifactsPopover = observer(({
    isRequestArtifactsModalOpen,
    closePopover,
    intl,
    vm,
}: IRequestArtifactsPopoverProps) => {

    const currentTabs: IRequestArtifactTabs[] = [
        {
            type: 'artifacts',
            label: requestArtifactsTabLabels.artifacts,
            permission: hasPermission(Permission.REQUEST_ARTIFACTS)
        },
        {
            type: 'replays',
            label: requestArtifactsTabLabels.replays,
            permission: hasPermission(Permission.REQUEST_PASSPORT_REPLAY)
        },
    ].filter(t => t.permission) as IRequestArtifactTabs[];

    useEffect(() => {
        if (vm) {
            vm.getRequestArtifactsMeetings(currentTabs[0].type);
        }
    }, [vm]);


    if (!vm) return null;
    const isDisabled: boolean = vm.shouldDisableRequest;

    return (
        <CommonModal
            title={intl.formatMessage({
                id: 'oe.artifacts.requestArtifacts',
                defaultMessage: 'Request Artifacts',
                description: 'Label text',
            })}
            className='request-artifacts-popover'
            isOpen={isRequestArtifactsModalOpen}
            onRequestClose={closePopover}
            footer={(
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultButton
                        text={intl.formatMessage({
                            id: 'oe.artifacts.requestArtifactsButtonLabel',
                            defaultMessage: `{defaultText}{artifactsTabMessage}{separator}{replaysTabMessage}`,
                            description: 'Label text',
                        }, {
                            artifactsTabMessage:`${
                                vm.isValidArtifactsTabSelection ?
                                'Request Artifacts for ' + vm.artifactsSelectedMeetingIds.size +
                                ` Meeting${vm.artifactsSelectedMeetingIds.size !== 1 ? 's' : ''}` :
                                ``
                            }`,
                            replaysTabMessage:`${
                                vm.replaysSelectedMeetingIds.size ?
                                'Request Replay for ' + vm.replaysSelectedMeetingIds.size +
                                ` Meeting${vm.replaysSelectedMeetingIds.size !== 1 ? 's' : ''}` :
                                ``
                            }`,
                            separator: vm.isValidArtifactsTabSelection && vm.replaysSelectedMeetingIds.size ? ', ' : '',
                            defaultText: `${vm.isValidArtifactsTabSelection || vm.replaysSelectedMeetingIds.size ?
                                '' :
                                'Request Artifacts'
                            }`
                        })}
                        modifier={['fat', 'confirm-safe']}
                        disabled={isDisabled}
                        onClick={vm.handleRequestMeetingsArtifactsInBatch}
                        dataTestId='request-artifacts-in-batch'
                    />
                    <DefaultCloseModalButton/>
                </div>
            )}
        >
            <div className='request-artifacts-popover-body'>
                {vm.isMeetingsLoading
                    ? <div className='request-artifacts-popover--loading'>
                        {
                            intl.formatMessage({
                                description: 'Text',
                                defaultMessage: 'Loading meetings...',
                                id: 'oe.common.loadingMeetings'
                            })
                        }
                    </div>
                    : <>
                        <RequestArtifactsPopoverHeader
                            currentTabs={currentTabs}
                            selectedTab={vm.selectedTab}
                            setSelectedTab={vm.setSelectedTab}
                        />
                        <RequestArtifactsContentWrapper
                            vm={vm}
                            intl={intl}
                        />
                    </>
                }
            </div>

        </CommonModal>
    );
});

export default RequestArtifactsPopover;

export interface IRequestArtifactsContentWrapperProps {
    intl: IntlShape;
    vm: RequestArtifactsPopoveriewModel;
}

export const RequestArtifactsContentWrapper = observer(({
    intl,
    vm,
}: IRequestArtifactsContentWrapperProps) => {

    return vm.selectedTab === 'artifacts'
        ? <ArtifactsTab intl={intl} vm={vm} />
        : <ReplaysTab intl={intl} vm={vm} />
    ;
});
